import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight, ArrowUpRight, Headset, Database, Send, Globe2, Boxes, Megaphone,
  CheckCircle2, ChevronDown, Loader2, Sparkles, ShieldCheck, Zap,
  RotateCw, ClipboardCheck, Plug,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const SERVICES = [
  {
    id: "bpo", n: "01", Icon: Headset, name: "BPO & Managed Operations", tag: "People + process",
    metric: { label: "Calls handled today", base: 4128, tick: 3 },
    blurb: "Outsourced contact-centre and back-office teams that plug directly into your acquisition funnel — lead qualification, appointment setting, customer service and claims processing, run under Connexis QA.",
    bullets: ["UK-hours qualification & appointment desks", "Script design, QA scoring and call recording", "CRM-native dispositioning and handoffs", "Per-seat or per-outcome commercial models"],
    engage: "From 2 seats · monthly rolling",
    fit: "You're generating enquiries but losing them between first contact and a booked appointment.",
  },
  {
    id: "data", n: "02", Icon: Database, name: "Data Provision & Enrichment", tag: "Compliant audiences",
    metric: { label: "Records enriched today", base: 18420, tick: 7 },
    blurb: "GDPR-audited consumer and business datasets, suppression washing, enrichment and custom audience builds — sourced, scored and delivered ready for campaign use.",
    bullets: ["GDPR-audited sourcing with consent trails", "TPS / CTPS suppression washing", "Enrichment, scoring & de-duplication", "Custom audience builds by sector & geography"],
    engage: "Per-record or licensed · volume pricing",
    fit: "You have the sales capacity but no compliant data to feed it.",
  },
  {
    id: "messaging", n: "03", Icon: Send, name: "SMS & Email Solutions", tag: "Managed messaging",
    metric: { label: "Messages sent today", base: 72190, tick: 24 },
    blurb: "Fully-managed SMS and email infrastructure — sender reputation, deliverability engineering, compliance-scripted copy and journey automation, reported per campaign.",
    bullets: ["Managed sender reputation & deliverability", "Compliance-scripted copy (FCA / GDPR aware)", "Journey automation & re-engagement flows", "Per-campaign delivery and conversion reporting"],
    engage: "Per-message + management fee · monthly",
    fit: "Your follow-up and nurture is leaking revenue between touchpoints.",
  },
  {
    id: "web", n: "04", Icon: Globe2, name: "Web Development", tag: "Conversion-first builds",
    metric: { label: "Pages A/B live", base: 34, tick: 0 },
    blurb: "High-conversion landing pages, funnels and brand sites — built by the team that runs acquisition for a living, so every page is engineered around a measurable conversion event.",
    bullets: ["Conversion-first landing pages & funnels", "A/B testing & experimentation setup", "Analytics, pixel and consent wiring", "Fast, SEO-ready production builds"],
    engage: "Fixed-scope project · 2–6 weeks",
    fit: "Traffic arrives but your pages don't convert it.",
  },
  {
    id: "systems", n: "05", Icon: Boxes, name: "System Builds", tag: "Custom platforms",
    metric: { label: "Systems in prod", base: 27, tick: 0 },
    blurb: "Client portals, CRMs, billing engines and routing systems — the same class of infrastructure Connexis itself runs on, designed and built for your operation with ongoing SLAs.",
    bullets: ["Client portals & role-based dashboards", "Billing, wallet & payout systems", "API, webhook & integration layers", "Ongoing SLAs, monitoring & iteration"],
    engage: "Scoped build + monthly SLA",
    fit: "You've outgrown spreadsheets and off-the-shelf CRMs.",
  },
  {
    id: "media", n: "06", Icon: Megaphone, name: "Paid Media & Creative", tag: "Managed acquisition",
    metric: { label: "Ad spend managed", base: 148920, tick: 12, prefix: "£" },
    blurb: "Fully-managed Meta and Google campaigns with the creative baked in — ad concepts, copy and content production, run by the team that prices financial leads for a living.",
    bullets: ["Meta & Google campaign management", "Ad creative, copy & content production", "Landing page alignment & CRO", "Transparent per-campaign P&L reporting"],
    engage: "Ad spend + management fee · monthly",
    fit: "You want a managed acquisition engine — not another set of dashboards to learn.",
  },
  {
    id: "reactivation", n: "07", Icon: RotateCw, name: "Lead Reactivation & Database Revival", tag: "Dormant-to-live",
    metric: { label: "Records revived today", base: 3241, tick: 5 },
    blurb: "Your old CRM is a gold mine. We pull stale, aged and never-contacted records, wash them for compliance, and run a multi-channel reactivation campaign to convert dormant data into live, booked appointments.",
    bullets: ["Full database audit + TPS/CTPS suppression", "Consent re-permissioning where required", "Multi-touch SMS + email + AI voice sequences", "Booked-appointment or per-conversion pricing"],
    engage: "Per-record + performance fee · 4-8 week sprints",
    fit: "You've got 10k+ old records sitting unused in a CRM.",
  },
  {
    id: "compliance", n: "08", Icon: ClipboardCheck, name: "Compliance & Call QA", tag: "Regulatory operations",
    metric: { label: "QA calls scored today", base: 892, tick: 2 },
    blurb: "Managed call-recording QA, script compliance scoring and evidence packs for FCA CONC, ICO, ICOBS and MCOB — the same review process Connexis runs on its own consumer brands, offered as a service.",
    bullets: ["Random-sample call QA scoring, weekly reports", "Script + adviser compliance benchmarking", "Consent audit + DSAR / SAR handling support", "Regulator-ready evidence packs on demand"],
    engage: "Fixed monthly · unlimited sample size",
    fit: "You're growing fast and your QA can't keep pace with volume.",
  },
  {
    id: "crm", n: "09", Icon: Plug, name: "CRM Setup & Integration", tag: "Pipes, plumbed",
    metric: { label: "CRMs live", base: 62, tick: 0 },
    blurb: "Native HubSpot, Salesforce, Pipedrive, GHL, Zoho and Zapier build-outs — lead ingestion, deal automation, task orchestration and reporting dashboards, engineered by the team that runs the Connexis routing fabric.",
    bullets: ["Native connector build + webhook fan-out", "Pipeline, deal-stage and task automation", "Reporting dashboards + attribution tracking", "Handover, training and 30-day post-launch SLA"],
    engage: "Fixed-scope project · 1-3 weeks",
    fit: "Leads land in your CRM but nothing happens next.",
  },
];

const FIT_CHIPS = [
  { label: "We lose leads between enquiry and appointment", id: "bpo" },
  { label: "We need compliant data to target", id: "data" },
  { label: "Our follow-up & nurture is leaking", id: "messaging" },
  { label: "Our landing pages don't convert", id: "web" },
  { label: "We've outgrown our CRM & spreadsheets", id: "systems" },
  { label: "We need someone to run our ads & creative", id: "media" },
  { label: "We've got a dead CRM full of old records", id: "reactivation" },
  { label: "Our QA can't keep up with call volume", id: "compliance" },
  { label: "Leads land in our CRM but nothing happens", id: "crm" },
];

export default function Services() {
  const rain = useCodeRain("services", "dark");
  const [openId, setOpenId] = useState("bpo");
  const [enquiryService, setEnquiryService] = useState("");
  const panelRefs = useRef({});
  const formRef = useRef(null);

  const openAndScroll = (id) => {
    setOpenId(id);
    setTimeout(() => panelRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
  };

  const discuss = (id) => {
    setEnquiryService(id);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/40" data-testid="services-hero">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="absolute -top-40 -right-24 w-[680px] h-[680px] rounded-full bg-[#3d7bff]/[0.07] blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-28">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Managed Services
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[62px] tracking-[-0.045em] font-semibold text-slate-900 leading-[1.03]">
                Beyond leads.{" "}
                <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">
                  The full operational stack.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-[17px] text-slate-600 leading-relaxed">
                Lead generation is our core product. These nine services are the machinery around it —
                the operations, data, messaging, media, systems and integrations we run internally, offered to the brands we supply.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  data-testid="services-hero-discuss-cta"
                  onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                  className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
                >
                  Discuss a project <ArrowUpRight className="size-4" />
                </Button>
                <a href="#stack">
                  <Button variant="outline" data-testid="services-hero-explore-cta" className="rounded-full px-5 h-11 border-slate-300 hover:border-[#0055FF]/40">
                    Explore the stack <ArrowRight className="size-4" />
                  </Button>
                </a>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-2"><ShieldCheck className="size-3.5 text-[#0055FF]" /> GDPR & FCA-aware delivery</span>
                <span className="inline-flex items-center gap-2"><Zap className="size-3.5 text-[#0055FF]" /> Run by acquisition operators</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-[#0055FF]" /> One accountable team</span>
              </div>
            </div>
            <StackVisual onPick={openAndScroll} />
          </div>
        </div>
      </section>

      {/* ── FIT SELECTOR ─────────────────────────────────── */}
      <section className="border-b border-slate-200/70 bg-slate-50/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mr-1">Where are you stuck?</span>
            {FIT_CHIPS.map((c) => (
              <button
                key={c.id}
                data-testid={`services-fit-chip-${c.id}`}
                onClick={() => openAndScroll(c.id)}
                className={`rounded-full border px-4 py-2 text-[13px] font-medium transition-all ${
                  openId === c.id
                    ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF] shadow-[0_2px_12px_rgba(0,85,255,0.15)]"
                    : "border-slate-300 bg-white text-slate-700 hover:border-[#0055FF]/40 hover:text-slate-900"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE STACK — expandable panels ─────────────────── */}
      <section id="stack" className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">The stack</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Nine services. One operator.</h2>
            <p className="mt-4 text-slate-600 text-lg">Each service stands alone — together they form the acquisition machinery we run for financial brands.</p>
          </div>

          <div className="mt-12 space-y-4">
            {SERVICES.map((s) => {
              const open = openId === s.id;
              return (
                <div
                  key={s.id}
                  ref={(el) => (panelRefs.current[s.id] = el)}
                  data-testid={`service-panel-${s.id}`}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                    open ? "border-[#0055FF]/30 bg-white shadow-[0_16px_50px_-20px_rgba(0,85,255,0.25)]" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <button
                    data-testid={`service-panel-toggle-${s.id}`}
                    onClick={() => setOpenId(open ? "" : s.id)}
                    className="w-full flex items-center gap-5 px-6 sm:px-8 py-6 text-left"
                  >
                    <span className={`hidden sm:block font-mono text-sm tabular-nums ${open ? "text-[#0055FF]" : "text-slate-400"}`}>{s.n}</span>
                    <span className={`grid place-items-center size-11 rounded-xl shrink-0 transition-colors ${open ? "bg-[#0055FF] text-white" : "bg-slate-900 text-white"}`}>
                      <s.Icon className="size-5" strokeWidth={1.6} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-lg sm:text-xl font-medium tracking-tight text-slate-900">{s.name}</span>
                      <span className="block mt-0.5 text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-400">{s.tag}</span>
                    </span>
                    <ChevronDown className={`size-5 text-slate-400 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#0055FF]" : ""}`} />
                  </button>

                  <div className={`grid transition-[grid-template-rows] duration-500 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="px-6 sm:px-8 pb-8 sm:pl-[7.5rem]">
                        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8">
                          <div>
                            <p className="text-[15px] text-slate-600 leading-relaxed">{s.blurb}</p>
                            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                              {s.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                  <CheckCircle2 className="size-4 text-[#0055FF] mt-0.5 shrink-0" /> {b}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-5 self-start">
                            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Best fit when</div>
                            <p className="mt-2 text-sm text-slate-700 leading-relaxed">{s.fit}</p>
                            <div className="mt-4 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Engagement</div>
                            <p className="mt-1.5 text-sm font-medium text-slate-900">{s.engage}</p>
                            <Button
                              data-testid={`service-discuss-${s.id}`}
                              onClick={() => discuss(s.id)}
                              className="mt-5 w-full rounded-full h-10 bg-[#0055FF] hover:bg-[#0044CC]"
                            >
                              Discuss this service <ArrowUpRight className="size-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW ENGAGEMENTS WORK ──────────────────────────── */}
      <section className="relative overflow-hidden py-20 bg-slate-900 text-white">
        {rain.on && <CodeRain variant="dark" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#60A5FA]">How it works</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.03em] font-medium">Scoped like infrastructure, not agency retainers.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "1", t: "Scope call", d: "A 30-minute call to map your operation, constraints and the outcome you're buying — not a sales pitch." },
              { n: "2", t: "Fixed proposal", d: "A written scope with fixed pricing, timelines and success measures. No open-ended billing." },
              { n: "3", t: "Operate & report", d: "We build or run the service under SLA, with the same per-campaign reporting our lead buyers get." },
            ].map((x) => (
              <div key={x.n} className="rounded-2xl border border-white/10 bg-[#131b2b] p-7">
                <span className="font-mono text-sm text-[#60A5FA]">0{x.n}</span>
                <h3 className="mt-3 text-lg font-medium">{x.t}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENQUIRY FORM ──────────────────────────────────── */}
      <section ref={formRef} id="enquire" className="py-20 sm:py-28 bg-slate-50/60 border-t border-slate-200/70">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Start a conversation</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Tell us where you're stuck.</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              Every engagement starts with a scope call — we'll tell you honestly whether a managed service,
              a system build, or simply buying leads is the right move for your stage.
            </p>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <Sparkles className="size-4 text-[#0055FF]" />
                <span className="text-sm font-medium text-slate-900">Leads remain our core product</span>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                If acquisition volume is your bottleneck, start there —{" "}
                <Link to="/buy-leads" className="text-[#0055FF] font-medium hover:underline" data-testid="services-buyleads-link">
                  build a lead order in 60 seconds →
                </Link>
              </p>
            </div>
          </div>
          <EnquiryForm service={enquiryService} setService={setEnquiryService} />
        </div>
      </section>
    </div>
  );
}

/* ── Hero stack visual · Operations Control ────────────────────────
 * All 9 service tiles arranged in a 3×3 grid — each showing a
 * live-ticking micro-metric — with an animated circuit-board layer
 * behind and a floating "Connexis Core" ops chip pinned to the corner.
 * Purely visual, no network calls; deterministic ticker seeded at mount.
 */
function StackVisual({ onPick }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative hidden lg:block" data-testid="services-stack-visual">
      <style>{`
        @keyframes svcCorePulse {
          0%, 100% { box-shadow: 0 0 20px rgba(0,85,255,0.35), 0 0 55px rgba(0,85,255,0.18); }
          50%      { box-shadow: 0 0 35px rgba(0,85,255,0.65), 0 0 90px rgba(0,85,255,0.32); }
        }
        @keyframes svcDash { to { stroke-dashoffset: -260; } }
        .svc-core { animation: svcCorePulse 2.6s ease-in-out infinite; }
        .svc-dash { stroke-dasharray: 3 7; animation: svcDash 14s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .svc-core, .svc-dash { animation: none; }
        }
      `}</style>

      {/* Background ambient glow + panel ring */}
      <div className="absolute inset-0 -m-6 rounded-[32px] bg-gradient-to-br from-[#0055FF]/[0.06] via-white to-white pointer-events-none" />
      <div className="absolute inset-0 -m-6 rounded-[32px] ring-1 ring-slate-200/70 pointer-events-none" />

      {/* Circuit-board background — a thin dashed grid overlay for depth */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 300 300"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="svcSpoke" x1="0" x2="1">
            <stop offset="0" stopColor="#0055FF" stopOpacity="0" />
            <stop offset="0.5" stopColor="#0055FF" stopOpacity="0.45" />
            <stop offset="1" stopColor="#0055FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Two diagonals + one horizontal, all animated — feels like a data bus */}
        <line x1="10"  y1="10"  x2="290" y2="290" stroke="url(#svcSpoke)" strokeWidth="1.1" className="svc-dash" />
        <line x1="290" y1="10"  x2="10"  y2="290" stroke="url(#svcSpoke)" strokeWidth="1.1" className="svc-dash" />
        <line x1="10"  y1="150" x2="290" y2="150" stroke="url(#svcSpoke)" strokeWidth="1.1" className="svc-dash" />
      </svg>

      {/* Floating "Connexis Core" corner chip — slim, minimal */}
      <div className="absolute -top-2 -right-2 z-20 inline-flex items-center gap-1.5 rounded-full bg-slate-900/95 ring-1 ring-white/10 backdrop-blur px-2.5 py-1 shadow-[0_8px_20px_-8px_rgba(15,23,42,0.6)]">
        <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[9.5px] uppercase tracking-[0.16em] font-semibold text-white/70">Core</span>
        <span className="text-[10px] font-mono tabular-nums text-white/95">{(240000 + tick * 4).toLocaleString()}/mo</span>
      </div>

      {/* 3×3 grid of service tiles */}
      <div className="relative grid grid-cols-3 gap-2.5">
        {SERVICES.map((s) => (
          <ServiceTile key={s.id} s={s} tick={tick} onPick={() => onPick(s.id)} />
        ))}
      </div>

      {/* Bottom status footer */}
      <div className="relative mt-5 flex items-center justify-between text-[11px] font-mono">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-600 shadow-soft">
          <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live ops · sub-100ms routing
        </div>
        <div className="text-slate-400 tabular-nums">
          uptime {" "}<span className="text-slate-900 font-semibold">99.99%</span>
        </div>
      </div>
    </div>
  );
}

function ServiceTile({ s, tick, onPick }) {
  const base = s.metric?.base ?? 0;
  const rate = s.metric?.tick ?? 0;
  const value = base + tick * rate;
  const formatted = (s.metric?.prefix ?? "") + value.toLocaleString();
  return (
    <button
      onClick={onPick}
      className="group relative flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-left transition-all duration-300 hover:border-[#0055FF]/50 hover:shadow-[0_18px_45px_-20px_rgba(0,85,255,0.4)] hover:-translate-y-0.5 min-h-[128px] overflow-hidden"
      data-testid={`stack-visual-row-${s.id}`}
    >
      <div className="flex items-center justify-between w-full">
        <span className="grid place-items-center size-8 rounded-lg bg-slate-900 text-white group-hover:bg-[#0055FF] transition-colors shrink-0">
          <s.Icon className="size-3.5" strokeWidth={1.7} />
        </span>
        <span className="font-mono text-[10px] text-slate-300 group-hover:text-[#0055FF] transition-colors">{s.n}</span>
      </div>
      <div className="block text-[11.5px] font-semibold text-slate-900 leading-tight">
        {s.name}
      </div>
      {s.metric && (
        <div className="mt-auto pt-1.5 border-t border-slate-100">
          <div className="flex items-baseline justify-between gap-1">
            <span className="font-mono text-[12.5px] font-semibold text-slate-900 tabular-nums">
              {formatted}
            </span>
            {rate > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-mono text-emerald-600 font-semibold">
                <svg width="7" height="7" viewBox="0 0 8 8"><path d="M4 1L7 6H1z" fill="currentColor" /></svg>
                live
              </span>
            )}
          </div>
          <div className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-semibold mt-0.5 truncate">
            {s.metric.label}
          </div>
        </div>
      )}
    </button>
  );
}

/* ── Enquiry form ──────────────────────────────────────── */
const SERVICE_OPTIONS = [
  { value: "bpo", label: "BPO & Managed Operations" },
  { value: "data", label: "Data Provision & Enrichment" },
  { value: "messaging", label: "SMS & Email Solutions" },
  { value: "web", label: "Web Development" },
  { value: "systems", label: "System Builds" },
  { value: "media", label: "Paid Media & Creative" },
  { value: "reactivation", label: "Lead Reactivation & Database Revival" },
  { value: "compliance", label: "Compliance & Call QA" },
  { value: "crm", label: "CRM Setup & Integration" },
  { value: "unsure", label: "Not sure yet — advise me" },
];

function EnquiryForm({ service, setService }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const successRef = useRef(null);

  useEffect(() => {
    if (sent) {
      setTimeout(() => successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    }
  }, [sent]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message");
      return;
    }
    setSending(true);
    try {
      const svc = SERVICE_OPTIONS.find((o) => o.value === (service || "unsure"))?.label || "Not sure yet";
      await api.post("/services/enquiry", { ...form, service: svc });
      setSent(true);
    } catch (_) {
      toast.error("Could not send — please try again");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div ref={successRef} className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-soft" data-testid="enquiry-success">
        <span className="mx-auto grid place-items-center size-12 rounded-full bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="size-6" />
        </span>
        <h3 className="mt-5 text-xl font-medium text-slate-900">Enquiry received.</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          We'll come back to you within one business day to set up a scope call. A confirmation is on its way to your inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-8 shadow-soft space-y-5" data-testid="services-enquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="enq-name" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</Label>
          <Input id="enq-name" data-testid="enquiry-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
        </div>
        <div>
          <Label htmlFor="enq-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Work email *</Label>
          <Input id="enq-email" data-testid="enquiry-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.co.uk" />
        </div>
      </div>
      <div>
        <Label htmlFor="enq-company" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Company</Label>
        <Input id="enq-company" data-testid="enquiry-company" className="mt-1.5" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company Ltd" />
      </div>
      <div>
        <Label htmlFor="enq-service" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Service</Label>
        <select
          id="enq-service"
          data-testid="enquiry-service"
          value={service || "unsure"}
          onChange={(e) => setService(e.target.value)}
          className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30"
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="enq-message" className="text-xs font-semibold uppercase tracking-wider text-slate-600">What are you trying to achieve? *</Label>
        <Textarea id="enq-message" data-testid="enquiry-message" className="mt-1.5 min-h-[110px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your operation, volumes and the outcome you need…" />
      </div>
      <Button type="submit" disabled={sending} data-testid="enquiry-submit" className="w-full rounded-full h-11 bg-[#0055FF] hover:bg-[#0044CC]">
        {sending ? <Loader2 className="size-4 animate-spin" /> : <>Send enquiry <ArrowUpRight className="size-4" /></>}
      </Button>
      <p className="text-[11px] text-slate-400 text-center">We reply within one business day. No newsletters, no spam.</p>
    </form>
  );
}
